import {
  domainPhase,
  recordToCreate,
  zoneOf,
  type TCustomDomain,
  type TCustomDomains,
} from '../../entities/custom-domain';
import { DnsRecords, FoundRecords } from './dns-records';
import { DomainSteps } from './domain-steps';

const Lead = ({ title, children }: { title: string; children: string }) => (
  <>
    <p className="text-data font-medium text-primary">{title}</p>
    <p className="mt-1 text-meta leading-17 text-muted">{children}</p>
  </>
);

// What to do about a hostname that does not point here yet, in the words of each cause.
const Problem = ({ domain, list }: { domain: TCustomDomain; list: TCustomDomains }) => {
  const record = recordToCreate(domain, list.cnameTarget, list.targetAddresses);
  const found = domain.dnsFound;
  const name = record.name;

  if (domain.dnsProblem === 'proxied') {
    return (
      <>
        <Lead title="The record is right, but proxied">
          Cloudflare answers for it instead of passing it to us, so the certificate cannot be
          issued.
        </Lead>
        <FoundRecords name={name} found={found} proxied />
        <p className="mt-3 text-meta text-primary">
          In Cloudflare, switch that record to “DNS only” — the grey cloud, not the orange one.
        </p>
      </>
    );
  }
  if (domain.dnsProblem === 'cname_elsewhere') {
    return (
      <>
        <Lead title={`${domain.hostname} points at another service`}>
          There is a CNAME already, for someone else.
        </Lead>
        <FoundRecords name={name} found={found} />
        <p className="mt-3 text-meta text-primary">
          Replace its value with {list.cnameTarget}. Whatever used it before stops receiving on this
          name.
        </p>
        <DnsRecords record={record} />
      </>
    );
  }
  if (domain.dnsProblem === 'address_elsewhere') {
    return (
      <>
        <Lead title={`${domain.hostname} points somewhere else`}>It resolves, but not to us.</Lead>
        <FoundRecords name={name} found={found} />
        <p className="mt-3 text-meta text-primary">Replace it with this record:</p>
        <DnsRecords record={record} />
      </>
    );
  }
  return (
    <>
      <p className="text-meta leading-17 text-muted">
        {domain.apex
          ? `${domain.hostname} is a root domain, which cannot hold a CNAME: add these A records at the company that runs its DNS.`
          : `Add this at the company that runs DNS for ${zoneOf(domain.hostname)}. It usually shows up in a few minutes.`}
      </p>
      <DnsRecords record={record} />
    </>
  );
};

export const DomainDetail = ({ domain, list }: { domain: TCustomDomain; list: TCustomDomains }) => {
  const phase = domainPhase(domain);

  if (phase === 'lost') {
    return (
      <>
        <Lead title={`${domain.hostname} stopped working`}>
          Its record no longer points here, so every address on this hostname is refusing requests.
          Their other addresses still work; put the record back and it recovers on its own.
        </Lead>
        <DnsRecords record={recordToCreate(domain, list.cnameTarget, list.targetAddresses)} />
      </>
    );
  }
  if (phase === 'certificate') {
    return (
      <>
        <Lead title="Issuing a certificate">
          The record is in place. This part is ours and usually takes under a minute.
        </Lead>
        <DomainSteps domain={domain} />
        <p className="mt-3 text-meta leading-17 text-faint">
          Until it finishes, the hostname answers nothing — nobody is sending to it yet, so nothing
          is lost.
          {domain.tlsError && <span className="block">{domain.tlsError}</span>}
        </p>
      </>
    );
  }
  if (phase === 'answering') {
    return (
      <>
        <Lead title="Routing the hostname">
          The certificate is ready. The hostname starts answering within a minute or two.
        </Lead>
        <DomainSteps domain={domain} />
      </>
    );
  }
  return (
    <>
      <Problem domain={domain} list={list} />
      <DomainSteps domain={domain} />
    </>
  );
};
